/**
 * v2.9.150 — useShazamModeMap hook
 * ------------------------------------------------------------------
 * Shared client cache for Shazam mode lookups. Components that
 * render a `<ShazamBadge>` per station (e.g. StationCard, the
 * Compare Stations table) call this hook with the JWT token and
 * receive a `Map<station_id, "mandatory" | "fallback">`.
 *
 * The hook fetches the active-stations list ONCE per 60-second
 * window across ALL consumers (singleton in-memory cache). This
 * avoids per-card N×fetch storms when the dashboard renders 100+
 * StationCards at the same time.
 *
 * On error / 401 it returns an empty Map silently; the badge then
 * renders nothing for every station.
 */
import { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const CACHE_TTL_MS = 60_000;

// Module-singleton cache shared across all hook callers
let _cache = {
  fetchedAt: 0,
  map: null,         // Map<sid, "mandatory" | "fallback">
  totals: null,      // { total, mandatory_count, fallback_count }
  inFlight: null,    // Promise<Map> while a fetch is happening
};

const fetchShazamMap = async (token) => {
  const now = Date.now();
  if (_cache.map && now - _cache.fetchedAt < CACHE_TTL_MS) {
    return { map: _cache.map, totals: _cache.totals };
  }
  if (_cache.inFlight) return _cache.inFlight;
  const promise = (async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(
        `${API}/api/admin/diagnostic/shazam-active-stations`,
        { headers }
      );
      if (!res.ok) {
        _cache.inFlight = null;
        return { map: new Map(), totals: null };
      }
      const json = await res.json();
      const m = new Map();
      (json.mandatory || []).forEach((s) => m.set(s.station_id, 'mandatory'));
      (json.fallback || []).forEach((s) => m.set(s.station_id, 'fallback'));
      _cache = {
        fetchedAt: Date.now(),
        map: m,
        totals: {
          total: json.total,
          mandatory_count: json.mandatory_count,
          fallback_count: json.fallback_count,
        },
        inFlight: null,
      };
      return { map: m, totals: _cache.totals };
    } catch (e) {
      _cache.inFlight = null;
      return { map: new Map(), totals: null };
    }
  })();
  _cache.inFlight = promise;
  return promise;
};

export const useShazamModeMap = (token) => {
  const [state, setState] = useState({ map: new Map(), totals: null });

  useEffect(() => {
    let active = true;
    fetchShazamMap(token).then((r) => {
      if (active) setState(r);
    });
    // Refresh every TTL window so cards stay in sync
    const id = setInterval(() => {
      fetchShazamMap(token).then((r) => {
        if (active) setState(r);
      });
    }, CACHE_TTL_MS);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [token]);

  return state; // { map, totals }
};

export default useShazamModeMap;
